import classNames from 'classnames';
import moment, { Moment } from 'moment';
import React, { Fragment, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

import {
  Enum,
  MemberPhoneDeletionReason,
  MemberPhoneInvalidateReason,
  MemberStatus,
  PatientGender,
  PatientLivingArrangement,
  PhoneType,
  ResidenceType,
  States,
} from '@vestahealthcare/common/enums';
import { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  Address,
  BaseEnum,
  ExternalIdSource,
  LanguagePreference,
  MemberExternalId,
  MemberPhone,
  Patient,
  Phone,
  SelfDirectingContact,
} from '@vestahealthcare/common/models';
import {
  isValidAddress,
  isValidCity,
  isValidZipCode,
} from '@vestahealthcare/common/utils/format';

import {
  Button,
  CollapsableSidebar,
  DateTimePicker,
  IconButton,
  PanelInfo,
  PhoneInput,
  Select,
  SelectPhoneOption,
  TextArea,
  TextInput,
} from 'styleguide-v2';

import { EditLanguages } from 'dash/src/components/EditLanguages';
import {
  MemberPhoneInvalidateParam,
  MemberPhoneUpdateParam,
  PatientParam,
  UpdateMemberPhoneParams,
} from 'dash/src/services/PatientServices';

import { useCommonStyles } from '../../styles';
import { DeletePhoneGroup } from './DeletePhoneGroup';
import { EditPhoneGroup } from './EditPhoneGroup';

const I18N_NAMESPACE = 'personalDetails.personalDetailsCard.modal';

type Props = {
  data: {
    externalIdSources: ExternalIdSource[];
  };
  externalIds?: MemberExternalId[];
  member: Patient;
  open?: boolean;
  onClose: () => void;
  onSubmit: (
    params: Partial<
      { member: PatientParam } & {
        proxyContact: SelfDirectingContact;
      } & UpdateMemberPhoneParams & { externalIds: MemberExternalId[] }
    >,
  ) => Promise<boolean>;
  phoneNumbers?: MemberPhone[];
  selfDirectingContact?: SelfDirectingContact;
};

type ItemID = {
  id: number | string;
};

const getStringErrorMessage = (value?: string) => {
  if (value?.trim()) {
    if (value.match(/\d/g)) {
      return translate('global.numbersNotAllowed');
    }

    return undefined;
  }
  return true;
};

const filterItemInList = (items: ItemID[], item?: ItemID, values?: ItemID[]) =>
  items
    .filter(Boolean)
    .filter((i) => i.id === item?.id || !values?.find((v) => v.id === i?.id));

export const MemberPersonalEditModal = ({
  data,
  externalIds: memberExternalIds,
  member,
  open,
  onClose,
  onSubmit,
  phoneNumbers: memberPhoneNumbers,
  selfDirectingContact: memberSelfDirectingContact,
}: Props) => {
  const commonStyles = useCommonStyles();

  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const isMemberEditable = member.isEditable();
  const isDemographicsEditable =
    isMemberEditable && member.isDemographicsEditable();

  const [firstName, setFirstName] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [birthDate, setBirthDate] = useState<Moment>();
  const [gender, setGender] = useState<PatientGender>();
  const [genderComment, setGenderComment] = useState<string>();
  const [externalIds, setExternalIds] = useState<MemberExternalId[]>([]);
  const [residence, setResidence] = useState<ResidenceType>();
  const [residenceOther, setResidenceOther] = useState<string>();
  const [address, setAddress] = useState<Partial<Address>>();
  const [livesWith, setLivesWith] = useState<PatientLivingArrangement[]>();
  const [livesWithOther, setLivesWithOther] = useState<string>();
  const [selfDirecting, setSelfDirecting] = useState<boolean>();
  const [selfDirectingContact, setSelfDirectingContact] = useState<
    Partial<SelfDirectingContact>
  >();
  const [languages, setLanguages] = useState<Partial<LanguagePreference>[]>([]);
  const [phones, setPhones] = useState<MemberPhone[]>([]);
  const [deletedPhones, setDeletedPhones] = useState<MemberPhoneUpdateParam[]>(
    [],
  );
  const [invalidPhones, setInvalidPhones] = useState<
    MemberPhoneInvalidateParam[]
  >([]);

  const preferredPhone = useMemo(
    () => phones?.find(({ preferred }) => preferred),
    [phones],
  );

  const isPatientEmrIntegrated =
    member.isEmrIntegrated() &&
    member.status &&
    [MemberStatus.ACCEPTED, MemberStatus.ACTIVE].includes(member.status);

  const isPatientCrmIntegrated =
    member.isCrmIntegrated() && member.status === MemberStatus.PASSIVE;

  const hasReadOnlyPhones = !!phones?.find(
    (phone) =>
      (isPatientEmrIntegrated && phone.isPhoneEmrIntegrated) ||
      (isPatientCrmIntegrated && phone.isPhoneCrmIntegrated),
  );

  const validateMemberInfo = () =>
    firstName &&
    !getStringErrorMessage(firstName) &&
    lastName &&
    !getStringErrorMessage(lastName) &&
    birthDate?.isValid() &&
    gender;

  const validateExternalIds = () =>
    externalIds.length > 1
      ? !externalIds.some(
          ({ externalId, externalIdSource }) =>
            !externalId || !externalIdSource,
        )
      : !!externalIds[0].externalId === !!externalIds[0].externalIdSource;

  const validateAddressInfo = () =>
    residence &&
    (residence !== ResidenceType.OTHER || residenceOther) &&
    address &&
    isValidAddress(address as Address);

  const validateLivesWith = () =>
    livesWith?.length &&
    (livesWith.every((item) => item !== PatientLivingArrangement.OTHER) ||
      livesWithOther);

  const validateSelfDirecting = () =>
    selfDirecting !== undefined &&
    (selfDirecting ||
      (selfDirectingContact?.firstName &&
        selfDirectingContact?.lastName &&
        selfDirectingContact?.phoneNumber &&
        selfDirectingContact?.phoneType &&
        selfDirectingContact?.infoOnFile !== undefined &&
        selfDirectingContact?.relationship));

  const validateLanguages = () =>
    languages.every((item) => !!item.language) &&
    languages.some((item) => item.preferred);

  const validatePhones = () =>
    !phones?.find(({ phone }) => !phone?.number || !phone?.type) &&
    !!preferredPhone &&
    ![...deletedPhones, ...invalidPhones].find(
      ({ reason, reasonOther }) =>
        !reason || (reason.value === BaseEnum.OTHER && !reasonOther),
    );

  const validate = () =>
    (!isDemographicsEditable || validateMemberInfo()) &&
    (!isDemographicsEditable || validateExternalIds()) &&
    (!isDemographicsEditable || validateAddressInfo()) &&
    validateLivesWith() &&
    validateSelfDirecting() &&
    validateLanguages() &&
    validatePhones();

  const doSubmit = async () => {
    setSubmitted(true);

    if (!validate()) return;

    setLoadingButton(true);
    try {
      const result = await onSubmit({
        member: {
          ...(isDemographicsEditable
            ? {
                address: {
                  ...address,
                  state: address?.state?.value,
                },
                birthDate,
                firstName,
                gender: {
                  type: gender?.value,
                  comment: genderComment,
                },
                lastName,
              }
            : {}),
          language: languages.find((l) => l.preferred)?.language?.value,
          languages: languages
            .map((l) => l.language?.value)
            .filter(Boolean) as string[],
          livingArrangement: livesWith?.map(({ value }) => value),
          livingArrangementOther: livesWithOther,
          residenceType: residence?.value,
          residenceTypeOther: residenceOther,
          selfDirecting,
        },

        /** PROXY CONTACT */
        proxyContact: selfDirectingContact as SelfDirectingContact,

        /** EXTERNAL IDs */
        externalIds: isDemographicsEditable ? externalIds : undefined,

        /** PHONES */
        phones: phones?.filter(({ inactivatedAt }) => !inactivatedAt),
        deletedPhones,
        inactivatedPhones: invalidPhones,
        reactivatedPhones: phones?.filter(
          ({ inactivatedAt }) => !!inactivatedAt,
        ),
      });
      if (result) {
        onClose();
      }
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    if (!member) return;

    setFirstName(member.firstName);
    setLastName(member.lastName);
    setBirthDate(moment(member.birthDate));
    setGender(member.gender);
    setGenderComment(member.genderComment);
    setResidence(member.residenceType);
    setResidenceOther(member.residenceTypeOther);
    setAddress(member.address || new Address({}));
    setLivesWith(member.livingArrangement);
    setLivesWithOther(member.livingArrangementOther);
    setSelfDirecting(member.selfDirecting);
    setSelfDirectingContact(
      memberSelfDirectingContact || new SelfDirectingContact({}),
    );
    setLanguages(
      member?.languages
        ?.map(
          (l) =>
            ({
              language: l,
              preferred: l === member.language,
            } as LanguagePreference),
        )
        ?.sort(({ preferred: a }, { preferred: b }) => {
          if (a) return -1;
          if (b) return 1;
          return 0;
        }) || [],
    );
  }, [member, memberSelfDirectingContact]);

  useEffect(() => {
    setExternalIds(
      memberExternalIds?.length
        ? [...memberExternalIds]
        : [new MemberExternalId({})],
    );
  }, [memberExternalIds]);

  useEffect(() => {
    if (!memberPhoneNumbers?.length) {
      setPhones([]);
      setInvalidPhones([]);
    } else {
      setPhones([
        ...memberPhoneNumbers
          .filter(MemberPhone.filterActive)
          .map((item) => MemberPhone.clone(item)),
      ]);
      setInvalidPhones([
        ...memberPhoneNumbers
          .filter((phone) => !MemberPhone.filterActive(phone))
          .map(
            (item) =>
              ({
                ...item,
                phone: Phone.clone(item.phone),
                reason: item.inactivatedReason
                  ? Enum.toSelectable([item.inactivatedReason])[0]
                  : undefined,
                reasonOther: item.inactivatedReasonOther,
              } as MemberPhoneUpdateParam),
          ),
      ]);
    }
  }, [memberPhoneNumbers]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setDeletedPhones([]);
        setLoadingButton(false);
        setSubmitted(false);
      }, 250);
    }
  }, [open]);

  const externalIdSources = externalIds
    .map((e) => e.externalIdSource)
    .filter(Boolean);

  return (
    <CollapsableSidebar
      title={translate(`${I18N_NAMESPACE}.title`)}
      open={!!open}
      onClose={onClose}
      size={650}
    >
      <CollapsableSidebar.Body>
        <div className="grid-wrapper fit">
          {!isDemographicsEditable && (
            <PanelInfo className="grid-span-12" type="info">
              {translate(`${I18N_NAMESPACE}.infoNotDemographicEditable`)}
            </PanelInfo>
          )}

          {member.isDemographicsEditable() && (
            <>
              <TextInput
                className="grid-span-6"
                disabled={!isDemographicsEditable}
                error={submitted && getStringErrorMessage(firstName)}
                label={translate(`${I18N_NAMESPACE}.firstName`)}
                onChange={setFirstName}
                required
                value={firstName}
              />
              <TextInput
                className="grid-span-6"
                disabled={!isDemographicsEditable}
                error={submitted && getStringErrorMessage(lastName)}
                label={translate(`${I18N_NAMESPACE}.lastName`)}
                onChange={setLastName}
                required
                value={lastName}
              />
              <DateTimePicker
                className="grid-span-12"
                disabled={isMemberEditable}
                disableClereable
                error={submitted && !birthDate}
                label={translate(`${I18N_NAMESPACE}.birthDate`)}
                maxDate={new Date()}
                onChange={(value?: Date) =>
                  setBirthDate(value ? moment(value.getTime()) : undefined)
                }
                placeholder={translate('global.chooseADate')}
                required
                value={birthDate?.toDate()}
              />
              <Select
                className="grid-span-12"
                disableClearable
                disabled={!isDemographicsEditable}
                error={submitted && !gender}
                label={translate(`${I18N_NAMESPACE}.gender`)}
                items={Enum.toSelectable(PatientGender.asArray)}
                onChange={(item?: Selectable) =>
                  setGender(item ? PatientGender.byKey[item.value] : undefined)
                }
                required
                value={gender ? Enum.toSelectable([gender])[0] : undefined}
              />
              <TextArea
                className="grid-span-12"
                disabled={!gender || !isDemographicsEditable}
                value={genderComment}
                label={translate(`${I18N_NAMESPACE}.genderComment`)}
                rows={3}
                onChange={setGenderComment}
              />

              {externalIds?.map((externalId, index) => (
                <Fragment key={externalId?.id ?? index}>
                  <TextInput
                    className="grid-span-6"
                    disabled={!isDemographicsEditable}
                    error={submitted && !externalId?.externalId}
                    label={
                      index === 0 ? (
                        <span
                          className="flex middle"
                          style={{ height: '17.14px' }}
                        >
                          {translate(`${I18N_NAMESPACE}.externalId`)}
                          {' * '}
                          {isDemographicsEditable &&
                            externalIds.length <
                              data.externalIdSources.length && (
                              <button
                                className={classNames(
                                  'flex middle',
                                  commonStyles.addLink,
                                )}
                                onClick={(evt) => {
                                  evt.preventDefault();
                                  setExternalIds([
                                    ...externalIds,
                                    new MemberExternalId({}),
                                  ]);
                                }}
                              >
                                <AddIcon
                                  className={commonStyles.addIcon}
                                  fontSize="small"
                                />
                                {translate(`${I18N_NAMESPACE}.externalIdAdd`)}
                              </button>
                            )}
                        </span>
                      ) : undefined
                    }
                    onChange={(val) => {
                      if (externalIds[index]) {
                        externalIds[index].externalId = val || '';
                        setExternalIds([...externalIds]);
                      }
                    }}
                    value={externalId.externalId}
                  />
                  <Select
                    className="grid-span-5"
                    disabled={!isDemographicsEditable}
                    error={submitted && !externalId?.externalIdSource?.id}
                    getItemLabel={(item?: ExternalIdSource) => item?.name || ''}
                    items={filterItemInList(
                      data.externalIdSources,
                      externalId.externalIdSource,
                      externalIdSources,
                    )}
                    label={
                      index === 0
                        ? translate(`${I18N_NAMESPACE}.externalIdSource`)
                        : undefined
                    }
                    onChange={(val: ExternalIdSource) => {
                      if (externalIds[index]) {
                        externalIds[index].externalIdSource = val;
                        setExternalIds([...externalIds]);
                      }
                    }}
                    value={externalId.externalIdSource}
                  />

                  {isDemographicsEditable && (
                    <IconButton
                      className={classNames(
                        'grid-span-1',
                        index === 0
                          ? commonStyles.firstIcon
                          : commonStyles.icon,
                      )}
                      onClick={() => {
                        if (externalIds.length === 1) {
                          setExternalIds([new MemberExternalId({})]);
                        } else {
                          externalIds.splice(index, 1);
                          setExternalIds([...externalIds]);
                        }
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="large" />
                    </IconButton>
                  )}
                </Fragment>
              ))}
            </>
          )}
          <Select
            className={`grid-span-${
              residence === ResidenceType.OTHER ? 6 : 12
            }`}
            disabled={!isMemberEditable}
            getItemLabel={Enum.toString}
            error={submitted && !residence}
            items={ResidenceType.asArray}
            label={translate(`${I18N_NAMESPACE}.residence`)}
            onChange={setResidence}
            required
            value={residence}
          />
          {residence === ResidenceType.OTHER && (
            <TextInput
              className="grid-span-6"
              disabled={!isMemberEditable}
              error={submitted && !residenceOther}
              label={translate('global.other')}
              onChange={setResidenceOther}
              required
              value={residenceOther}
            />
          )}
          {member.isDemographicsEditable() && (
            <>
              <TextInput
                className="grid-span-12"
                disabled={!isDemographicsEditable}
                error={submitted && !address?.address1}
                value={address?.address1}
                label={translate('personalDetails.addressModal.address1')}
                onChange={(address1?: string) =>
                  setAddress({ ...address, address1 })
                }
                required
              />

              <TextInput
                className="grid-span-12"
                disabled={!isDemographicsEditable}
                value={address?.address2}
                label={translate('personalDetails.addressModal.address2')}
                onChange={(address2?: string) =>
                  setAddress({ ...address, address2 })
                }
              />

              <TextInput
                className="grid-span-6"
                disabled={!isDemographicsEditable}
                error={
                  submitted &&
                  !isValidCity(address?.city ?? '') &&
                  (!address?.city || translate('global.cityFormatError'))
                }
                value={address?.city}
                label={translate('personalDetails.addressModal.city')}
                onChange={(city?: string) => setAddress({ ...address, city })}
                required
              />

              <Select
                className="grid-span-3"
                disabled={!isDemographicsEditable}
                error={submitted && !address?.state}
                getItemLabel={(item?: Enum) => item?.value || ''}
                items={States.asArray}
                label={translate('personalDetails.addressModal.state')}
                value={address?.state}
                onChange={(state?: States) => setAddress({ ...address, state })}
                required
              />

              <TextInput
                className="grid-span-3"
                disabled={!isDemographicsEditable}
                error={
                  submitted &&
                  !isValidZipCode(address?.zipCode ?? '') &&
                  (!address?.zipCode || translate('global.zipFormatError'))
                }
                value={address?.zipCode}
                label={translate('personalDetails.addressModal.zip')}
                maxLength={10}
                onChange={(zipCode?: string) =>
                  setAddress({ ...address, zipCode })
                }
                required
              />
            </>
          )}
          <Select
            className={`grid-span-${
              livesWith?.includes(PatientLivingArrangement.OTHER) ? 6 : 12
            }`}
            disabled={!isMemberEditable}
            getItemLabel={Enum.toString}
            error={submitted && !livesWith?.length}
            items={PatientLivingArrangement.asArray}
            label={translate(`${I18N_NAMESPACE}.livesWith`)}
            multiple
            onChange={setLivesWith}
            required
            value={livesWith}
          />
          {livesWith?.includes(PatientLivingArrangement.OTHER) && (
            <TextInput
              className="grid-span-6"
              disabled={!isMemberEditable}
              error={submitted && !livesWithOther}
              label={translate('global.other')}
              onChange={setLivesWithOther}
              required
              value={livesWithOther}
            />
          )}
          <Select.Choice
            className="grid-span-12"
            disabled={!isMemberEditable}
            disableClearable
            error={
              submitted &&
              (selfDirecting === undefined || selfDirecting === null)
            }
            label={translate(`${I18N_NAMESPACE}.selfDirecting`)}
            onChange={setSelfDirecting}
            required
            value={selfDirecting}
          />
          {!selfDirecting && selfDirecting !== null && (
            <>
              <TextInput
                className="grid-span-6"
                disabled={!isMemberEditable}
                error={submitted && !selfDirectingContact?.firstName}
                label={translate(`${I18N_NAMESPACE}.proxyContactFirstName`)}
                onChange={(firstName?: string) =>
                  setSelfDirectingContact({
                    ...selfDirectingContact,
                    firstName,
                  })
                }
                required
                value={selfDirectingContact?.firstName}
              />

              <TextInput
                className="grid-span-6"
                disabled={!isMemberEditable}
                error={submitted && !selfDirectingContact?.lastName}
                label={translate(`${I18N_NAMESPACE}.proxyContactLastName`)}
                onChange={(lastName?: string) =>
                  setSelfDirectingContact({
                    ...selfDirectingContact,
                    lastName,
                  })
                }
                required
                value={selfDirectingContact?.lastName}
              />

              <PhoneInput
                className="grid-span-6"
                disabled={!isMemberEditable}
                label={translate(`${I18N_NAMESPACE}.proxyContactPhone`)}
                onChange={(phoneNumber?: string) =>
                  setSelfDirectingContact({
                    ...selfDirectingContact,
                    phoneNumber,
                  })
                }
                required
                submitted={submitted}
                value={selfDirectingContact?.phoneNumber}
              />

              <Select
                className="grid-span-6"
                disabled={!isMemberEditable}
                error={submitted && !selfDirectingContact?.phoneType}
                getItemLabel={Enum.toString}
                label={translate(`${I18N_NAMESPACE}.proxyContactPhoneType`)}
                onChange={(phoneType?: PhoneType) =>
                  setSelfDirectingContact({
                    ...selfDirectingContact,
                    phoneType: phoneType?.value,
                  })
                }
                items={[PhoneType.HOME, PhoneType.CELL]}
                value={
                  selfDirectingContact?.phoneType
                    ? PhoneType.byKey[selfDirectingContact.phoneType]
                    : undefined
                }
                required
              />
              <Select.Choice
                className="grid-span-6"
                disabled={!isMemberEditable}
                error={
                  submitted && selfDirectingContact?.infoOnFile === undefined
                }
                label={translate(`${I18N_NAMESPACE}.proxyContactInfoOnFile`)}
                onChange={(infoOnFile?: boolean) =>
                  setSelfDirectingContact({
                    ...selfDirectingContact,
                    infoOnFile,
                  })
                }
                required
                value={selfDirectingContact?.infoOnFile}
              />
              <TextInput
                className="grid-span-6"
                disabled={!isMemberEditable}
                error={submitted && !selfDirectingContact?.relationship}
                label={translate(`${I18N_NAMESPACE}.proxyContactRelationship`)}
                onChange={(relationship?: string) =>
                  setSelfDirectingContact({
                    ...selfDirectingContact,
                    relationship,
                  })
                }
                value={selfDirectingContact?.relationship}
                required
              />
            </>
          )}
          <h4 className="grid-span-12">
            {translate(`${I18N_NAMESPACE}.languages`)}
          </h4>
          <EditLanguages
            className="grid-span-12"
            disabled={!isMemberEditable}
            disabledPreferred={
              !!member.isCrmIntegrated() || !member.isLanguageEditable()
            }
            languages={languages}
            submitted={submitted}
            onChange={setLanguages}
          />
          <h4 className="grid-span-12">
            {translate(`${I18N_NAMESPACE}.phoneNumbers`)}
          </h4>
          <Select
            className="grid-span-12"
            disabled={!isMemberEditable}
            error={submitted && !preferredPhone}
            items={phones?.filter(
              ({ phone, primary }) => primary && phone?.number && phone?.type,
            )}
            getItemLabel={(item: MemberPhone) =>
              item?.phone?.getDisplayNumber() || ''
            }
            label={translate(`${I18N_NAMESPACE}.preferredPhone`)}
            onChange={(phone: MemberPhone) => {
              phones.forEach((item) => {
                if (item === phone) {
                  item.preferred = true;
                } else {
                  item.preferred = false;
                }
              });
              setPhones([...phones]);
            }}
            renderOption={(item: MemberPhone) => (
              <SelectPhoneOption userPhone={item} />
            )}
            required
            value={preferredPhone}
          />
          <EditPhoneGroup
            className="grid-span-12"
            disabled={!isMemberEditable}
            getItemReadOnly={(phone) =>
              Boolean(
                (isPatientEmrIntegrated && phone?.isPhoneEmrIntegrated) ||
                  (isPatientCrmIntegrated && phone?.isPhoneCrmIntegrated),
              )
            }
            getItemReadOnlyPrimary={(phone) =>
              hasReadOnlyPhones &&
              !(
                (isPatientEmrIntegrated && phone?.isPhoneEmrIntegrated) ||
                (isPatientCrmIntegrated && phone?.isPhoneCrmIntegrated)
              )
            }
            onChange={(editPhones, invalidPhone, deletedPhone) => {
              if (deletedPhone)
                setDeletedPhones([...deletedPhones, deletedPhone]);
              if (invalidPhone)
                setInvalidPhones([...invalidPhones, invalidPhone]);
              setPhones(editPhones);
            }}
            phones={phones}
            submitted={submitted}
          />
          {invalidPhones?.find(
            ({ authenticationPhone, inactivatedAt, reactivatedAt }) =>
              authenticationPhone && (!inactivatedAt || reactivatedAt),
          ) && (
            <>
              <br />
              <PanelInfo
                className="grid-span-12"
                type="warning"
                title={translate(
                  `${I18N_NAMESPACE}.warningInactivateAuthPhone`,
                )}
              />
            </>
          )}
          {!!invalidPhones?.length && (
            <DeletePhoneGroup
              className="grid-span-12"
              disabled={!isMemberEditable}
              onChange={(invalidPhones, invalidPhone) => {
                if (invalidPhone) setPhones([...phones, invalidPhone]);
                setInvalidPhones([
                  ...invalidPhones.map((phone) => {
                    (phone as MemberPhoneInvalidateParam).forceUpdate =
                      phone.reason?.value !== phone.inactivatedReason?.value ||
                      phone.reasonOther !== phone.inactivatedReasonOther;
                    return phone;
                  }),
                ]);
              }}
              phones={invalidPhones}
              phoneLabel={translate(`${I18N_NAMESPACE}.invalidPhone`)}
              reasonLabel={translate(`${I18N_NAMESPACE}.invalidateReason`)}
              reasons={Enum.toSelectable(MemberPhoneInvalidateReason.asArray)}
              submitted={submitted}
            />
          )}
          {deletedPhones?.find(
            ({ authenticationPhone, inactivatedAt, reactivatedAt }) =>
              authenticationPhone && (!inactivatedAt || reactivatedAt),
          ) && (
            <>
              <br />
              <PanelInfo
                className="grid-span-12"
                type="warning"
                title={translate(`${I18N_NAMESPACE}.warningDeleteAuthPhone`)}
              />
            </>
          )}
          {!!deletedPhones?.length && (
            <DeletePhoneGroup
              className="grid-span-12"
              disabled={!isMemberEditable}
              onChange={(deletedPhones, deletedPhone) => {
                if (deletedPhone) setPhones([...phones, deletedPhone]);
                setDeletedPhones([...deletedPhones]);
              }}
              phones={deletedPhones}
              phoneLabel={translate(`${I18N_NAMESPACE}.deletedPhone`)}
              reasonLabel={translate(`${I18N_NAMESPACE}.deleteReason`)}
              reasons={Enum.toSelectable(MemberPhoneDeletionReason.asArray)}
              submitted={submitted}
            />
          )}
        </div>
      </CollapsableSidebar.Body>
      <CollapsableSidebar.Buttons>
        <Button color="tertiary" onClick={onClose}>
          {translate('global.close')}
        </Button>
        <Button color="secondary" loading={loadingButton} onClick={doSubmit}>
          {translate('global.save')}
        </Button>
      </CollapsableSidebar.Buttons>
    </CollapsableSidebar>
  );
};
